import { useEffect, useState } from 'react';
import {
  kbClear, kbDebugEnabled, kbEntryCount, kbFormatLog, kbLog, kbLogMark, kbSubscribe,
} from '../../utils/ui/keyboardReframeDebug';
import { EXEMPT_SELECTOR, predictKeyboardTop } from '../../utils/ui/inlineKeyboardGuard';

/**
 * Everything needed to answer "WHICH field was this, where did it sit, and did
 * iOS have any reason to pan?" — the gap that made the first round of device
 * captures unusable for auditing (every entry read `focusin tag=INPUT id=`,
 * so a whole-app walkthrough produced no per-surface map).
 *
 * Pairs with the `vv.scroll ot=NN` lines the overlay already records: if `b`
 * (the field's bottom edge) sits above `kb` (the predicted keyboard line) and
 * iOS panned anyway, the guard's prediction is what's wrong — which is exactly
 * the question the Cobrar/Estimates captures left open.
 *
 * Debug-only: every caller is behind `kbDebugEnabled()`.
 */
function describeFocusTarget(el) {
  const base = { tag: el?.tagName || '?' };
  if (!el || typeof el.getBoundingClientRect !== 'function') return base;
  const attr = (n) => el.getAttribute?.(n) || '';
  const r = el.getBoundingClientRect();
  return {
    ...base,
    // Identity, best-effort in descending usefulness. Placeholder is the
    // practical winner for search fields, which carry no testid.
    field: attr('data-testid') || el.id || attr('name') || attr('placeholder') || attr('aria-label') || attr('type') || '',
    // Readable keys, not the 1-2 char style the per-frame entries use: one of
    // these prints per FOCUS (not per frame), so legibility beats compactness —
    // and a bare `t=` would read as a second timestamp next to the `+ms` column.
    top: r.top,
    bot: r.bottom,
    kbTop: predictKeyboardTop(window.innerHeight),
    // Inside a modal/drawer? Those are exempt from the inline guard entirely.
    exempt: typeof el.closest === 'function' && el.closest(EXEMPT_SELECTOR) ? 1 : 0,
    // Which surface — pathname alone identifies the tab in the /app/:feature shell.
    path: window.location?.pathname || '',
  };
}

/**
 * Floating capture chip for the keyboard-reframe recorder (see
 * keyboardReframeDebug.js). Renders ONLY when `?kbdebug=1` enabled the flag.
 *
 * Also owns the RAW event listeners (visualViewport resize/scroll, focus
 * in/out, window resize) so the log shows what the OS reported before any
 * rAF coalescing/damping — the hook logs its committed frames separately,
 * and diffing the two streams is the whole diagnostic.
 */
export default function KeyboardDebugOverlay() {
  const [count, setCount] = useState(kbEntryCount());
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => kbSubscribe(setCount), []);

  useEffect(() => {
    if (!kbDebugEnabled() || typeof window === 'undefined') return undefined;
    const vv = window.visualViewport;
    const onVvResize = () => kbLog('vv.resize', { h: vv.height, ot: vv.offsetTop });
    const onVvScroll = () => kbLog('vv.scroll', { h: vv.height, ot: vv.offsetTop });
    const onWinResize = () => kbLog('win.resize', { innerH: window.innerHeight });
    // Capture-phase: scroll doesn't bubble, but capture still descends — this
    // sees EVERY element's scroll (the keyboard scrim's overflowY:auto, the
    // modal body, the document), which is exactly the "who moved" question.
    const onWinScroll = (e) => {
      const t = e.target;
      if (t === document || t === window) kbLog('doc.scroll', { y: window.scrollY });
      else kbLog('el.scroll', { el: t?.tagName || '?', st: t?.scrollTop });
    };
    const onFocusIn = (e) => {
      kbLogMark();
      kbLog('focusin', describeFocusTarget(e.target));
    };
    const onFocusOut = (e) => kbLog('focusout', { tag: e.target?.tagName || '?' });
    if (vv) {
      vv.addEventListener('resize', onVvResize);
      vv.addEventListener('scroll', onVvScroll);
    }
    window.addEventListener('resize', onWinResize);
    window.addEventListener('scroll', onWinScroll, true);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      if (vv) {
        vv.removeEventListener('resize', onVvResize);
        vv.removeEventListener('scroll', onVvScroll);
      }
      window.removeEventListener('resize', onWinResize);
      window.removeEventListener('scroll', onWinScroll, true);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  if (!kbDebugEnabled()) return null;

  const copy = async () => {
    const text = kbFormatLog();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard denied (non-secure context) — show the log for manual copy.
      setOpen(true);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 'calc(env(safe-area-inset-top, 0px) + 4px)', left: 4, zIndex: 30000, fontFamily: 'ui-monospace, Menlo, monospace' }}>
      <div style={{ display: 'flex', gap: 4 }}>
        <button type="button" onClick={() => setOpen((o) => !o)} style={chipStyle}>
          KB {count}
        </button>
        <button type="button" onClick={copy} style={{ ...chipStyle, background: copied ? '#1c7c46' : '#2b5db8' }}>
          {copied ? 'Copied!' : 'Copy'}
        </button>
        <button type="button" onClick={kbClear} style={{ ...chipStyle, background: '#666' }}>
          Clear
        </button>
      </div>
      {open && (
        <pre style={panelStyle}>{kbFormatLog() || '(empty — tap a text field in a modal)'}</pre>
      )}
    </div>
  );
}

const chipStyle = {
  border: 'none',
  borderRadius: 6,
  background: '#111',
  color: '#fff',
  fontSize: 10,
  fontWeight: 700,
  padding: '4px 8px',
  opacity: 0.85,
  fontFamily: 'inherit',
};

const panelStyle = {
  margin: '4px 0 0',
  maxWidth: 'calc(100vw - 16px)',
  maxHeight: '45vh',
  overflow: 'auto',
  background: 'rgba(0,0,0,0.88)',
  color: '#9fe89f',
  fontSize: 9,
  lineHeight: 1.35,
  padding: 8,
  borderRadius: 8,
  whiteSpace: 'pre',
};
